import type { SocketType } from "../../types";
import { FLOW_CONTROL_SOCKET_TYPE } from "../../types";
import type { DynamicSocketDefinition } from "./socketDefinition";

/**
 * `fan_in` ("Await") socket derivation.
 *
 * Two things vary and both have to agree everywhere (canvas, save path,
 * template instantiation, the orchestrator's join-site assembly):
 *
 * 1. **Mode** flips the result socket between `array<T>` and bare `T`, and
 *    decides whether a separate `error` socket exists at all.
 * 2. **Element type `T`** does NOT come from config — it comes from the wire
 *    feeding the step's input. `fan_in` is the exact type-level inverse of
 *    fanout (`array<T> → T` per child); it lifts `T → array<T>` at the join.
 *
 * See `docs/workflow-closures-design.md` § "`fan_in` — the gather / join step".
 */

/**
 * Step type id. Declared here rather than imported from the step module so the
 * orchestrator and the socket dispatch can reference it without pulling in the
 * step registry (which would close an import cycle).
 */
export const FAN_IN_STEP_TYPE = "fan_in";

/** Stable config enum keys; the palette maps them to human labels. */
export const FAN_IN_MODES = [
  "all_success",
  "all_settled",
  "first_success",
  "first_settled",
] as const;

export type FanInMode = (typeof FAN_IN_MODES)[number];

export const DEFAULT_FAN_IN_MODE: FanInMode = "all_success";

export const FAN_IN_MODE_LABELS: Record<FanInMode, string> = {
  all_success: "All must succeed",
  all_settled: "All — keep partial results",
  first_success: "First to succeed",
  first_settled: "First to finish",
};

export const FAN_IN_MODE_DESCRIPTIONS: Record<FanInMode, string> = {
  all_success:
    "Wait for every fanned child. Any failure routes to the error output instead of results.",
  all_settled:
    "Wait for every fanned child and keep partial results — each entry records whether that child fulfilled or rejected.",
  first_success:
    "The first child to succeed wins; remaining children are cancelled. Routes to error only if every child fails.",
  first_settled:
    "The first child to finish wins, success or failure; remaining children are cancelled.",
};

/** The step's single input socket id. */
export const FAN_IN_INPUT_SOCKET_ID = "value";
/** Result socket id in the `all_*` modes (`array<T>`). */
export const FAN_IN_RESULTS_SOCKET_ID = "results";
/** Result socket id in the `first_*` modes (bare `T`). */
export const FAN_IN_RESULT_SOCKET_ID = "result";
/** Routable failure branch. Absent in `all_settled` — failures ride in the results. */
export const FAN_IN_ERROR_SOCKET_ID = "error";

/** `first_success` / `first_settled` return one `T` and cancel the losers. */
export function isFirstMode(mode: FanInMode): boolean {
  return mode === "first_success" || mode === "first_settled";
}

/** Only `all_settled` embeds failures in its results, so only it has no `error` socket. */
export function hasErrorSocket(mode: FanInMode): boolean {
  return mode !== "all_settled";
}

/**
 * Read the mode off a step config, degrading to the default.
 *
 * Generators run on config that has not necessarily been validated (the save
 * path regenerates sockets from client-sent config), so an unknown value must
 * not throw here.
 */
export function resolveFanInMode(config: Record<string, unknown>): FanInMode {
  const mode = config.mode;
  return typeof mode === "string" &&
    (FAN_IN_MODES as readonly string[]).includes(mode)
    ? (mode as FanInMode)
    : DEFAULT_FAN_IN_MODE;
}

/**
 * Lift a concrete socket type `T` to `array<T>` — monomorphization at wire time.
 *
 * Returns `null` when `T` is not resolved (nothing wired yet, or the topology
 * is not a single fanout region). An unresolved `T` is deliberately NOT
 * represented as `array<json>`: that would drop the element-type check publish
 * validation depends on, letting an incompatible downstream wire pass
 * validation and fail at runtime. Save still succeeds; publish is blocked
 * until `T` is concrete.
 *
 * Types that ARE resolved but have no array spelling in `SocketTypeSchema`
 * (`date`, and any already-array type, since `array<array<…>>` is not
 * expressible) widen to `array<json>`. That is a known-type widening, not an
 * unresolved fallback — refusing them outright would make fan-in unusable over
 * a whole class of legitimate wires.
 */
export function liftToArraySocketType(
  elementType: SocketType | undefined | null,
): SocketType | null {
  if (!elementType) return null;
  // A flow-control wire carries no value, so there is nothing to gather.
  if (elementType === FLOW_CONTROL_SOCKET_TYPE) return null;

  if (elementType.startsWith("entity_reference")) {
    return `array<${elementType}>` as SocketType;
  }

  switch (elementType) {
    case "string":
    case "number":
    case "boolean":
    case "uuid":
    case "json":
      return `array<${elementType}>` as SocketType;
    // Defensive. Both resolvers that feed this — the save path's
    // `buildFanInInputTypesByStepId` and the editor's `resolveFanInInputType` —
    // treat `any` as UNRESOLVED and drop it before calling, so a fan-in never
    // widens through here in practice. Kept because `any` is resolved-but-wide
    // if it ever does arrive, and there is no `array<any>` in the vocabulary:
    // `array<json>` is the honest lift, not a silent unresolved fallback.
    case "any":
      return "array<json>";
    default:
      // `date` and nested arrays — see the doc comment above.
      return "array<json>";
  }
}

/**
 * Generate the `fan_in` output sockets from its mode and its resolved input
 * element type.
 *
 * `resolvedInputType` is the concrete type of the socket wired into
 * `value`. Every other dynamic generator derives sockets from config alone;
 * this one needs the wire too, which is why the dispatch signature widens.
 *
 * When `T` is unresolved the result socket is omitted entirely rather than
 * emitted untyped — the canvas shows the node with no result to wire, and
 * publish validation reports the unresolved fan-in.
 */
function buildFanInResultSocket(
  mode: FanInMode,
  resolvedInputType: SocketType | undefined | null,
): DynamicSocketDefinition | null {
  if (isFirstMode(mode)) {
    // `first_*` returns one child's value, so the socket carries `T` itself
    // rather than the lifted array. It still has to make the two refusals the
    // lift makes — unresolved, and a flow-control wire that carries no value —
    // since it does not route through `liftToArraySocketType`.
    if (!resolvedInputType) return null;
    if (resolvedInputType === FLOW_CONTROL_SOCKET_TYPE) return null;
    return {
      id: FAN_IN_RESULT_SOCKET_ID,
      name: "Result",
      type: resolvedInputType,
      required: false,
      description:
        mode === "first_success"
          ? "Output of the first child that succeeded"
          : "Output of the first child that finished",
    };
  }

  const arrayType = liftToArraySocketType(resolvedInputType);
  if (!arrayType) return null;

  const settled = mode === "all_settled";
  return {
    id: FAN_IN_RESULTS_SOCKET_ID,
    name: "Results",
    // `all_settled` wraps each element in a `ChildOutcome<T>` envelope, so the
    // element type is no longer `T` — it is an object carrying fanoutIndex +
    // status + (value | error).
    type: settled ? "array<json>" : arrayType,
    required: false,
    description: settled
      ? "One entry per fanned child in fanout order, each recording fulfilled/rejected"
      : "One entry per fanned child, in fanout order",
  };
}

function buildFanInErrorSocket(mode: FanInMode): DynamicSocketDefinition {
  return {
    id: FAN_IN_ERROR_SOCKET_ID,
    name: "Error",
    type: "json",
    required: false,
    description:
      mode === "all_success"
        ? "Taken when any child failed; the result output is pruned"
        : "Taken when the deciding child failed; the result output is pruned",
  };
}

export function generateFanInOutputSockets(
  config: Record<string, unknown>,
  resolvedInputType?: SocketType | null,
): DynamicSocketDefinition[] {
  const mode = resolveFanInMode(config);
  const result = buildFanInResultSocket(mode, resolvedInputType);

  return [
    ...(result ? [result] : []),
    ...(hasErrorSocket(mode) ? [buildFanInErrorSocket(mode)] : []),
  ];
}
